/**
 * Book API — «Лаборатория превращений»
 * All endpoints under /api/book (reader) and /api/admin/book (editor+).
 * Uses axios instance from lib/api which auto-attaches Bearer token.
 */
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function authHeaders() {
  const token = localStorage.getItem("anokina_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ------- Reader (user-facing) -------
export async function getVolumeEntry(slugOrId) {
  const res = await axios.get(`${API}/api/book/${encodeURIComponent(slugOrId)}/entry`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function readBookPage(volumeSlug, pageId) {
  const res = await axios.get(
    `${API}/api/book/${encodeURIComponent(volumeSlug)}/pages/${pageId}`,
    { headers: authHeaders() }
  );
  return res.data;
}

export async function searchBook(volumeSlug, q) {
  const res = await axios.get(`${API}/api/book/${encodeURIComponent(volumeSlug)}/search`, {
    headers: authHeaders(), params: { q },
  });
  return res.data;
}

export async function toggleBookmark(volumeId, pageId) {
  const res = await axios.post(`${API}/api/book/bookmarks/toggle`,
    { volume_id: volumeId, page_id: pageId }, { headers: authHeaders() });
  return res.data;
}

export async function toggleLike(volumeId, pageId) {
  const res = await axios.post(`${API}/api/book/likes/toggle`,
    { volume_id: volumeId, page_id: pageId }, { headers: authHeaders() });
  return res.data;
}

export async function listBookmarks(volumeId) {
  const res = await axios.get(`${API}/api/book/bookmarks`, {
    headers: authHeaders(), params: volumeId ? { volume_id: volumeId } : {},
  });
  return res.data;
}

export async function createBookSupportRequest(payload) {
  const res = await axios.post(`${API}/api/book/support-requests`, payload,
    { headers: authHeaders() });
  return res.data;
}

// ------- Admin -------
export async function adminListVolumes() {
  const res = await axios.get(`${API}/api/admin/book/volumes`, { headers: authHeaders() });
  return res.data;
}
export async function adminCreateVolume(payload) {
  const res = await axios.post(`${API}/api/admin/book/volumes`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminUpdateVolume(id, payload) {
  const res = await axios.patch(`${API}/api/admin/book/volumes/${id}`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminDeleteVolume(id) {
  const res = await axios.delete(`${API}/api/admin/book/volumes/${id}`, { headers: authHeaders() });
  return res.data;
}

export async function adminListSections(volumeId) {
  const res = await axios.get(`${API}/api/admin/book/sections`,
    { headers: authHeaders(), params: volumeId ? { volume_id: volumeId } : {} });
  return res.data;
}
export async function adminCreateSection(payload) {
  const res = await axios.post(`${API}/api/admin/book/sections`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminUpdateSection(id, payload) {
  const res = await axios.patch(`${API}/api/admin/book/sections/${id}`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminDeleteSection(id) {
  const res = await axios.delete(`${API}/api/admin/book/sections/${id}`, { headers: authHeaders() });
  return res.data;
}

export async function adminListSubsections(params) {
  const res = await axios.get(`${API}/api/admin/book/subsections`,
    { headers: authHeaders(), params: params || {} });
  return res.data;
}
export async function adminCreateSubsection(payload) {
  const res = await axios.post(`${API}/api/admin/book/subsections`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminUpdateSubsection(id, payload) {
  const res = await axios.patch(`${API}/api/admin/book/subsections/${id}`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminDeleteSubsection(id) {
  const res = await axios.delete(`${API}/api/admin/book/subsections/${id}`, { headers: authHeaders() });
  return res.data;
}

export async function adminListPages(params) {
  const res = await axios.get(`${API}/api/admin/book/pages`,
    { headers: authHeaders(), params: params || {} });
  return res.data;
}
export async function adminGetPage(id) {
  const res = await axios.get(`${API}/api/admin/book/pages/${id}`, { headers: authHeaders() });
  return res.data;
}
export async function adminCreatePage(payload) {
  const res = await axios.post(`${API}/api/admin/book/pages`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminUpdatePage(id, payload) {
  const res = await axios.patch(`${API}/api/admin/book/pages/${id}`, payload, { headers: authHeaders() });
  return res.data;
}
export async function adminDeletePage(id) {
  const res = await axios.delete(`${API}/api/admin/book/pages/${id}`, { headers: authHeaders() });
  return res.data;
}

export async function adminReorder(kind, items) {
  const res = await axios.post(`${API}/api/admin/book/reorder`,
    { kind, items }, { headers: authHeaders() });
  return res.data;
}

export async function adminListSupportRequests() {
  const res = await axios.get(`${API}/api/admin/book/support-requests`, { headers: authHeaders() });
  return res.data;
}

// ------- Media upload (reuses platform admin upload) -------
export async function uploadBookImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API}/api/admin/media/upload-file`, fd, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return res.data; // { id, file_url, ... }
}

export const PAGE_LAYOUTS = [
  { key: "A", label: "A · Hero + текст",       hint: "Атмосферные главы, вступления, история." },
  { key: "B", label: "B · Изображение слева",  hint: "Изображение и текст одинаково важны." },
  { key: "C", label: "C · Изображение справа", hint: "Зеркальный editorial-вариант." },
  { key: "D", label: "D · Editorial статья",   hint: "Глубокие текстовые главы." },
  { key: "E", label: "E · Рецепт / технология", hint: "Полноценная рецептура." },
  { key: "F", label: "F · Сравнение / таблица", hint: "Сравнения, таблицы, схемы." },
];
